import { ChatCircleText, CheckCircle, PaperPlaneRight } from '@phosphor-icons/react';
import { FEEDBACK_MESSAGE_MAX, type FeedbackInput, type FeedbackKind } from '@shh/shared';
import {
  Button,
  Checkbox,
  ChoiceGroup,
  Icon,
  Surface,
  Text,
  TextArea,
  buttonStyles,
} from '@shh/ui';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useLens } from '../app/lens';
import { friendlyError, sendFeedback } from '../data/api';
import { locale } from '../lib/locale';
import { useFamily } from '../data/family';
import { feedbackText as t } from '../i18n/feedback';
import { APP_VERSION } from '../lib/releases';

/** Parents and teens – never a child, never the family screen on the wall (only with the PIN). */
export function useCanFeedback(): boolean {
  const f = useFamily();
  const lens = useLens();
  return lens.canParent || f.role === 'teen';
}

/** Links to this page say where they came from – that view goes along as context. */
export const feedbackLink = (from: string) => ({ to: '/feedback', state: { from } });

function device(isWall: boolean): FeedbackInput['context']['device'] {
  if (isWall) return 'wall';
  const touch = window.matchMedia('(pointer: coarse)').matches;
  if (!touch) return 'desktop';
  return Math.min(window.screen.width, window.screen.height) >= 600 ? 'tablet' : 'phone';
}

const installed = () =>
  Boolean((navigator as Navigator & { standalone?: boolean }).standalone) ||
  window.matchMedia('(display-mode: standalone)').matches;

const KINDS: FeedbackKind[] = ['bug', 'improvement', 'feature'];
const RATINGS = ['1', '2', '3', '4', '5'] as const;

/** "Ideen & Feedback": families tell us what is broken, could be better or is missing. */
export function FeedbackPage() {
  const lens = useLens();
  const allowed = useCanFeedback();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [rating, setRating] = useState<string>('');
  const [kind, setKind] = useState<FeedbackKind>('improvement');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState(false);
  const [quote, setQuote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<{ contact: boolean } | null>(null);

  if (!allowed) {
    return (
      <div className="flex flex-col items-start gap-gap-md">
        <Icon icon={ChatCircleText} size="xl" className="text-text-muted" />
        <Text>{t.locked}</Text>
      </div>
    );
  }

  if (sent) {
    return (
      <Surface className="mx-auto flex w-full max-w-narrow flex-col items-start gap-gap-md">
        <Icon icon={CheckCircle} size="xl" weight="duotone" className="text-feedback-success" />
        <p role="status" className="font-display text-xl font-black">
          {t.thanks}
        </p>
        {sent.contact && <Text tone="muted">{t.thanksContact}</Text>}
        <div className="flex flex-wrap gap-gap-sm">
          <Link to={from} className={buttonStyles({ variant: 'primary' })}>
            {t.back}
          </Link>
          <Button
            variant="quiet"
            onPress={() => {
              setSent(null);
              setMessage('');
              setRating('');
            }}
          >
            {t.another}
          </Button>
        </div>
      </Surface>
    );
  }

  const empty = message.trim() === '' && rating === '';
  const submit = async () => {
    if (empty) {
      setError(t.empty);
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await sendFeedback({
        kind,
        rating: rating ? Number(rating) : null,
        message: message.trim(),
        contact,
        quote,
        locale, // which consent text was on screen
        context: {
          view: from.slice(0, 100),
          version: APP_VERSION,
          device: device(lens.isWall),
          installed: installed(),
        },
      });
      setSent({ contact });
      setContact(false);
      setQuote(false);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="mx-auto flex w-full max-w-narrow flex-col gap-gap-lg"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Text>{t.intro}</Text>
      <ChoiceGroup
        label={t.ratingLabel}
        description={t.ratingHint}
        choices={RATINGS.map((r) => ({ value: r, label: r }))}
        value={rating}
        onChange={setRating}
      />
      <ChoiceGroup
        label={t.kindLabel}
        choices={KINDS.map((k) => ({
          value: k,
          label: t.kinds[k].label,
          description: t.kinds[k].hint,
        }))}
        value={kind}
        onChange={setKind}
      />
      <TextArea
        label={t.messageLabel}
        placeholder={t.placeholders[kind]}
        value={message}
        onChange={(v) => {
          setMessage(v);
          if (error === t.empty) setError(undefined);
        }}
        maxLength={FEEDBACK_MESSAGE_MAX}
        rows={6}
      />
      <div className="flex flex-col gap-gap-xs">
        <Checkbox isSelected={contact} onChange={setContact}>
          {t.consent.contact}
        </Checkbox>
        <Checkbox isSelected={quote} onChange={setQuote}>
          {t.consent.quote}
        </Checkbox>
      </div>
      <Text size="sm" tone="muted">
        {t.meta}
      </Text>
      <div className="flex flex-col items-start gap-gap-xs">
        <Button type="submit" icon={PaperPlaneRight} isDisabled={busy}>
          {busy ? t.sending : t.send}
        </Button>
        <Text size="sm" tone="muted">
          {t.sendHint}
        </Text>
        {error && (
          <p role="alert" className="text-sm font-semibold text-feedback-danger">
            {error}
          </p>
        )}
      </div>
    </form>
  );
}
